import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Alert,
  FormControlLabel,
  Switch,
  Button,
  Tabs,
  Tab,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Divider,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';
import { getTimezoneDisplayName, getUserTimezone } from '../utils/dateUtils';
import {
  filterCurrencyOptions,
  formatCurrencyPickerLabel,
  getIso4217CurrencyCodes
} from '../utils/iso4217';
import WorkingCalendarsSettingsPanel from '../components/WorkingCalendarsSettingsPanel';

const DISCLAIMER_COPY: Record<string, string> = {
  'currency.disclaimer.reference_indicative':
    'Platform-sourced figures are reference / indicative. Cross-rates shown are computed from the EUR-based reference unless the organization overrides.',
  'currency.disclaimer.org_responsibility':
    'Organization-defined rates are entered under the organization’s own responsibility.',
  'currency.disclaimer.not_legal_advice':
    'This is not tax, legal, or accounting advice.',
  'currency.disclaimer.eur_hub':
    'Internal math may use EUR as a hub; displayed pairs are equivalent to that method for the rates in use.'
};

const OrganizationSettings: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { formatDateTime, toDateTimeLocal, fromDateTimeLocal, preferences } = useDateFormat();
  const userTz = getUserTimezone(preferences);
  const userTzLabel = getTimezoneDisplayName(userTz);
  const timesShownInUtcOnly = userTz === 'UTC' || userTz === 'Etc/UTC';
  const dateTimeFieldHelperText = timesShownInUtcOnly
    ? 'Stored as UTC. Your User preferences use UTC, so this matches server storage.'
    : `Interpreted in your User preference timezone (${userTzLabel}), then saved as UTC. The browser control may look 24-hour even when you use 12-hour display elsewhere.`;
  const orgDateTimeExplainer = useMemo(
    () => (
      <Alert severity="info" sx={{ mb: 2 }}>
        {timesShownInUtcOnly ? (
          <Typography variant="body2">
            Your <strong>User preferences</strong> use timezone UTC. Dates and times in organization settings match how values are stored on the server
            (UTC).
          </Typography>
        ) : (
          <Typography variant="body2">
            Effective dates and times are <strong>stored in UTC</strong> on the server. They are <strong>shown</strong> using your User preferences:
            timezone <strong>{userTzLabel}</strong>, date format, and 12- or 24-hour clock. Date-time fields interpret your entry in that timezone before
            saving.
          </Typography>
        )}
      </Alert>
    ),
    [timesShownInUtcOnly, userTzLabel]
  );
  const [tab, setTab] = useState(0);
  const [org, setOrg] = useState<{ id: string; name?: string; timesheetApprovalsEnabled?: boolean; timeZone?: string } | null>(null);
  const [orgTimeZoneDraft, setOrgTimeZoneDraft] = useState('UTC');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [canManageCurrency, setCanManageCurrency] = useState(false);
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const [defaultCurrency, setDefaultCurrency] = useState('');
  const [defaultDraft, setDefaultDraft] = useState('');
  const [additional, setAdditional] = useState<{ id: string; currencyCode: string }[]>([]);
  const [defaultCurrencyPlatformUpdatedAt, setDefaultCurrencyPlatformUpdatedAt] = useState<string | null>(null);
  const [pairs, setPairs] = useState<
    {
      otherCurrency: string;
      impliedDefaultPer1Other: string | null;
      effectiveDefaultPer1Other: string | null;
      missingPlatformReference: boolean;
      missingEffectiveLeg: boolean;
      followPlatformRate: boolean;
      rateSource: 'platform' | 'override';
      rateUpdatedAt: string | null;
    }[]
  >([]);
  const [disclaimerKeys, setDisclaimerKeys] = useState<string[]>([]);
  const [overrideOther, setOverrideOther] = useState('');
  const [overrideRate, setOverrideRate] = useState('');
  const [overrideFrom, setOverrideFrom] = useState('');
  const [overrideTo, setOverrideTo] = useState('');
  const [pendingAdditional, setPendingAdditional] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTitle, setHistoryTitle] = useState('');
  const [historyRows, setHistoryRows] = useState<Record<string, unknown>[]>([]);
  const [historyKind, setHistoryKind] = useState<'default' | 'overrides'>('default');
  const [editOpen, setEditOpen] = useState(false);
  const defaultSaveTimerRef = useRef<number | null>(null);

  type OrgHourlyRow = {
    id: string;
    kind: 'EMPLOYEE_COST' | 'BILLING';
    effectiveFrom: string;
    amount: string;
    currencyCode: string;
  };
  const [hourlyLoading, setHourlyLoading] = useState(false);
  const [hourlyRows, setHourlyRows] = useState<OrgHourlyRow[]>([]);
  const [hourlyDialogOpen, setHourlyDialogOpen] = useState(false);
  const [hourlyDialogKind, setHourlyDialogKind] = useState<'EMPLOYEE_COST' | 'BILLING'>('EMPLOYEE_COST');
  const [hourlyEditId, setHourlyEditId] = useState<string | null>(null);
  const [hourlyFormFrom, setHourlyFormFrom] = useState('');
  const [hourlyFormAmount, setHourlyFormAmount] = useState('');
  const [hourlyFormCurrency, setHourlyFormCurrency] = useState('');

  const currencyCodes = useMemo(() => getIso4217CurrencyCodes(), []);
  const defaultCurrencyOptions = useMemo(() => {
    const code = defaultDraft.trim().toUpperCase();
    if (code.length === 3 && !currencyCodes.includes(code)) {
      return [code, ...currencyCodes];
    }
    return currencyCodes;
  }, [currencyCodes, defaultDraft]);
  const additionalPickerOptions = useMemo(() => {
    const ex = new Set(additional.map((a) => a.currencyCode.toUpperCase()));
    if (defaultDraft.trim().length === 3) {
      ex.add(defaultDraft.trim().toUpperCase());
    }
    return filterCurrencyOptions(currencyCodes, ex);
  }, [additional, currencyCodes, defaultDraft]);

  const loadOrg = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getOrganization(organizationId);
      const data = res?.data ?? res;
      setOrg(data ? { id: data.id || organizationId, name: data.name, timesheetApprovalsEnabled: !!data.timesheetApprovalsEnabled, timeZone: data.timeZone || 'UTC' } : null);
      setOrgTimeZoneDraft(data?.timeZone || 'UTC');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load organization settings');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  const resolveCurrencyAccess = useCallback(async () => {
    if (!organizationId) return;
    try {
      const profile = await api.getProfile();
      const systemRole = profile?.data?.user?.systemRole;
      if (systemRole === 'PLATFORM_ADMIN') {
        setCanManageCurrency(true);
        return;
      }
      const uo = await api.getUserOrganizations();
      const list = uo?.data ?? uo ?? [];
      const row = Array.isArray(list) ? list.find((o: any) => o.organizationId === organizationId) : null;
      const role = (row?.role || 'MEMBER').toUpperCase();
      setCanManageCurrency(['OWNER', 'MANAGER'].includes(role));
    } catch {
      setCanManageCurrency(false);
    }
  }, [organizationId]);

  const loadCurrency = useCallback(async () => {
    if (!organizationId) return;
    setCurrencyLoading(true);
    setError(null);
    try {
      const res = await api.getOrganizationCurrency(organizationId);
      const data = res?.data ?? res;
      setDefaultCurrency((data?.defaultCurrency as string) || '');
      setDefaultDraft((data?.defaultCurrency as string) || '');
      setDefaultCurrencyPlatformUpdatedAt((data?.defaultCurrencyPlatformUpdatedAt as string) || null);
      setAdditional(data?.additionalCurrencies || []);
      setPairs(
        (data?.pairs || []).map((p: Record<string, unknown>) => ({
          otherCurrency: String(p.otherCurrency || ''),
          impliedDefaultPer1Other: (p.impliedDefaultPer1Other as string) ?? null,
          effectiveDefaultPer1Other: (p.effectiveDefaultPer1Other as string) ?? null,
          missingPlatformReference: !!p.missingPlatformReference,
          missingEffectiveLeg: !!p.missingEffectiveLeg,
          followPlatformRate: p.followPlatformRate !== false,
          rateSource: (p.rateSource as 'platform' | 'override') || 'platform',
          rateUpdatedAt: (p.rateUpdatedAt as string) || null
        }))
      );
      setDisclaimerKeys(data?.disclaimerKeys || []);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('You do not have access to currency settings for this organization.');
      } else {
        setError(err.response?.data?.message || 'Failed to load currency settings');
      }
    } finally {
      setCurrencyLoading(false);
    }
  }, [organizationId]);

  const loadOrgHourlyRates = useCallback(async () => {
    if (!organizationId) return;
    setHourlyLoading(true);
    setError(null);
    try {
      const res = await api.getOrganizationHourlyRates(organizationId);
      const data = res?.data ?? res;
      const list = Array.isArray(data) ? data : [];
      setHourlyRows(
        list.map((r: Record<string, unknown>) => ({
          id: String(r.id),
          kind: r.kind as 'EMPLOYEE_COST' | 'BILLING',
          effectiveFrom: String(r.effectiveFrom),
          amount: String(r.amount),
          currencyCode: String(r.currencyCode || '')
        }))
      );
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('You do not have access to default rates for this organization.');
      } else {
        setError(err.response?.data?.message || 'Failed to load default rates');
      }
      setHourlyRows([]);
    } finally {
      setHourlyLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadOrg();
  }, [loadOrg]);

  useEffect(() => {
    resolveCurrencyAccess();
  }, [resolveCurrencyAccess]);

  useEffect(() => {
    if (tab === 1 && organizationId) {
      loadCurrency();
    }
  }, [tab, organizationId, loadCurrency]);

  useEffect(() => {
    if (tab === 2 && organizationId) {
      loadOrgHourlyRates();
    }
  }, [tab, organizationId, loadOrgHourlyRates]);

  const handleTimesheetApprovalsChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    if (!organizationId || !org) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.updateOrganization(organizationId, { timesheetApprovalsEnabled: enabled });
      setOrg((prev) => (prev ? { ...prev, timesheetApprovalsEnabled: enabled } : null));
      setSuccess(enabled ? 'Timesheet approvals enabled.' : 'Timesheet approvals disabled.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const handleOrgTimeZoneSave = async () => {
    if (!organizationId || !org) return;
    const tz = orgTimeZoneDraft.trim() || 'UTC';
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.updateOrganization(organizationId, { timeZone: tz });
      setOrg((prev) => (prev ? { ...prev, timeZone: tz } : null));
      setSuccess('Organization timezone updated.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update timezone');
    } finally {
      setSaving(false);
    }
  };

  const scheduleDefaultCurrencySave = (code: string) => {
    if (!organizationId || !canManageCurrency) return;
    const upper = code.trim().toUpperCase();
    if (upper.length !== 3 || !defaultCurrencyOptions.includes(upper)) return;
    if (defaultSaveTimerRef.current) window.clearTimeout(defaultSaveTimerRef.current);
    defaultSaveTimerRef.current = window.setTimeout(async () => {
      try {
        setSaving(true);
        setError(null);
        setSuccess(null);
        await api.patchOrganizationDefaultCurrency(organizationId, upper);
        setDefaultCurrency(upper);
        setSuccess('Organization default currency updated.');
        await loadCurrency();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to update default currency');
      } finally {
        setSaving(false);
      }
    }, 650);
  };

  const addAdditional = async () => {
    if (!organizationId || !canManageCurrency) return;
    const code = (pendingAdditional || '').trim().toUpperCase();
    if (code.length !== 3 || !currencyCodes.includes(code)) {
      setError('Choose a valid ISO 4217 currency from the list.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.addOrganizationAdditionalCurrency(organizationId, code);
      setPendingAdditional(null);
      setAddDialogOpen(false);
      await loadCurrency();
      setSuccess(`Added ${formatCurrencyPickerLabel(code)}.`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add currency');
    } finally {
      setSaving(false);
    }
  };

  const removeAdditional = async (code: string) => {
    if (!organizationId || !canManageCurrency) return;
    setSaving(true);
    setError(null);
    try {
      await api.removeOrganizationAdditionalCurrency(organizationId, code, false);
      await loadCurrency();
      setSuccess(`Removed ${code}.`);
    } catch (err: any) {
      const blockers = err.response?.data?.data?.blockers;
      if (blockers?.length) {
        setError(
          `Cannot remove: ${blockers.map((b: any) => `${b.type} (${b.count})`).join(', ')}. Reassign companies, accounts, or tasks using this currency, or contact platform support.`
        );
      } else {
        setError(err.response?.data?.message || 'Failed to remove currency');
      }
    } finally {
      setSaving(false);
    }
  };

  const submitOverride = async () => {
    if (!organizationId || !canManageCurrency) return;
    if (!overrideOther || !overrideRate || !overrideFrom) {
      setError('Enter rate and effective from date.');
      return;
    }
    const fromIso = fromDateTimeLocal(overrideFrom);
    const toIso = overrideTo.trim() ? fromDateTimeLocal(overrideTo) : undefined;
    setSaving(true);
    setError(null);
    try {
      await api.patchOrganizationAdditionalFollow(organizationId, overrideOther, false);
      await api.postOrganizationFxOverride(organizationId, {
        otherCurrency: overrideOther,
        defaultPer1Other: overrideRate.trim(),
        effectiveFrom: fromIso,
        effectiveTo: toIso
      });
      setOverrideRate('');
      setOverrideTo('');
      setEditOpen(false);
      await loadCurrency();
      setSuccess('Exchange rate saved. Auto from server is off for this currency.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save rate');
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (segment: string, title: string) => {
    if (!organizationId) return;
    setHistoryTitle(title);
    try {
      const res = await api.getOrganizationCurrencyHistory(
        organizationId,
        segment === 'default' ? 'default' : segment.toUpperCase()
      );
      const data = res?.data ?? res;
      const kind = data?.kind as 'default' | 'overrides';
      setHistoryKind(kind === 'overrides' ? 'overrides' : 'default');
      setHistoryRows((data?.entries as Record<string, unknown>[]) || []);
      setHistoryOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load history');
    }
  };

  const pullFromPlatform = async (code: string) => {
    if (!organizationId || !canManageCurrency) return;
    if (
      !window.confirm(
        'Align this currency with the platform reference? Organization-specific rate history for this code will be removed.'
      )
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.postOrganizationPullPlatformRate(organizationId, code);
      await loadCurrency();
      setSuccess(`Updated ${code} from platform reference.`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to pull from server');
    } finally {
      setSaving(false);
    }
  };

  const setFollowPlatform = async (code: string, follow: boolean) => {
    if (!organizationId || !canManageCurrency) return;
    setSaving(true);
    setError(null);
    try {
      await api.patchOrganizationAdditionalFollow(organizationId, code, follow);
      await loadCurrency();
      setSuccess(follow ? `${code} now follows the platform reference.` : `${code} can use organization rates.`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const openHourlyDialog = (kind: 'EMPLOYEE_COST' | 'BILLING', row: OrgHourlyRow | null) => {
    setHourlyDialogKind(kind);
    if (row) {
      setHourlyEditId(row.id);
      setHourlyFormFrom(toDateTimeLocal(row.effectiveFrom));
      setHourlyFormAmount(row.amount);
      setHourlyFormCurrency(row.currencyCode.toUpperCase());
    } else {
      setHourlyEditId(null);
      setHourlyFormFrom(toDateTimeLocal(new Date().toISOString()));
      setHourlyFormAmount('');
      setHourlyFormCurrency((defaultCurrency || 'USD').toUpperCase());
    }
    setHourlyDialogOpen(true);
  };

  const saveHourlyDialog = async () => {
    if (!organizationId || !canManageCurrency) return;
    const iso = fromDateTimeLocal(hourlyFormFrom);
    const code = hourlyFormCurrency.trim().toUpperCase();
    if (code.length !== 3 || !currencyCodes.includes(code)) {
      setError('Choose a valid ISO 4217 currency.');
      return;
    }
    if (!hourlyFormAmount.trim()) {
      setError('Enter an amount (use 0 if needed).');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (hourlyEditId) {
        await api.patchOrganizationHourlyRate(organizationId, hourlyEditId, {
          effectiveFrom: iso,
          amount: hourlyFormAmount.trim(),
          currencyCode: code
        });
        setSuccess('Rate row updated.');
      } else {
        await api.createOrganizationHourlyRate(organizationId, {
          kind: hourlyDialogKind,
          effectiveFrom: iso,
          amount: hourlyFormAmount.trim(),
          currencyCode: code
        });
        setSuccess('Rate row added.');
      }
      setHourlyDialogOpen(false);
      await loadOrgHourlyRates();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save rate');
    } finally {
      setSaving(false);
    }
  };

  const deleteHourlyRow = async (row: OrgHourlyRow) => {
    if (!organizationId || !canManageCurrency) return;
    if (!window.confirm('Delete this rate row? Gaps resolve as zero until another row applies.')) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteOrganizationHourlyRate(organizationId, row.id);
      setSuccess('Rate row deleted.');
      await loadOrgHourlyRates();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  const renderHourlyTable = (kind: 'EMPLOYEE_COST' | 'BILLING', title: string, description: string) => {
    const rows = hourlyRows.filter((r) => r.kind === kind);
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
                {description}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => openHourlyDialog(kind, null)}
              disabled={!canManageCurrency || saving}
            >
              Add row
            </Button>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Effective from</TableCell>
                  <TableCell>Amount / hour</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        No rows — effective rate is 0 until you add one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{formatDateTime(row.effectiveFrom)}</TableCell>
                      <TableCell>{row.amount}</TableCell>
                      <TableCell>{row.currencyCode}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openHourlyDialog(kind, row)}
                            disabled={!canManageCurrency || saving}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => deleteHourlyRow(row)}
                            disabled={!canManageCurrency || saving}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const openEditRate = (otherCurrency: string) => {
    setOverrideOther(otherCurrency);
    const p = pairs.find((x) => x.otherCurrency === otherCurrency);
    setOverrideRate(p?.effectiveDefaultPer1Other || p?.impliedDefaultPer1Other || '');
    setOverrideFrom(toDateTimeLocal(new Date().toISOString()));
    setOverrideTo('');
    setEditOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error && !org) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 2 }}>
        Organization settings
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="General" />
        <Tab label="Currency" />
        <Tab label="Default rates" />
        <Tab label="Calendars" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {tab === 0 && (
        <Card sx={{ maxWidth: 640 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Timesheet approvals
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Master switch for this organization. When on, users can submit timesheets and managers can approve or reject them. When off, approval features are hidden and disabled; existing data is not changed.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={!!org?.timesheetApprovalsEnabled}
                  onChange={handleTimesheetApprovalsChange}
                  disabled={saving}
                  color="primary"
                />
              }
              label={org?.timesheetApprovalsEnabled ? 'Approvals enabled' : 'Approvals disabled'}
            />
            {saving && <LinearProgress sx={{ mt: 1, maxWidth: 200 }} />}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Organization timezone
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Default IANA timezone for budget period evaluation. Accounts inherit this unless they set their own timezone.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                label="Timezone"
                value={orgTimeZoneDraft}
                onChange={(e) => setOrgTimeZoneDraft(e.target.value)}
                placeholder="UTC"
                helperText={`Current: ${org?.timeZone || 'UTC'}`}
                sx={{ minWidth: 280 }}
              />
              <Button variant="contained" onClick={handleOrgTimeZoneSave} disabled={saving}>
                Save timezone
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {tab === 1 && (
        <Box>
          {orgDateTimeExplainer}
          {!canManageCurrency && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Only organization owners and managers can change currency settings.
            </Alert>
          )}

          {currencyLoading ? (
            <LinearProgress />
          ) : (
            <>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Organization currencies
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
                        Set the organization default, then add currencies you work with. Rates show how many units of your default equal one unit
                        of the other currency. When &quot;Auto from server&quot; is on, the pair follows the platform reference; turn it off to set
                        organization-specific rates (with effective dates).
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setPendingAdditional(null);
                        setAddDialogOpen(true);
                      }}
                      disabled={!canManageCurrency || saving || !defaultCurrency}
                    >
                      Add currency
                    </Button>
                  </Box>

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: 200 }}>Currency</TableCell>
                          <TableCell sx={{ minWidth: 140 }}>
                            Exchange rate
                            <Typography component="span" variant="caption" display="block" color="text.secondary">
                              {defaultCurrency || 'Default'} per 1 other
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 160 }}>Updated</TableCell>
                          <TableCell>Source</TableCell>
                          <TableCell align="center">Auto from server</TableCell>
                          <TableCell align="right" sx={{ minWidth: 200 }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Organization default
                            </Typography>
                            <Autocomplete
                              options={defaultCurrencyOptions}
                              value={defaultDraft.length === 3 ? defaultDraft.toUpperCase() : null}
                              onChange={(_, v) => {
                                const c = v ? v.toUpperCase() : '';
                                setDefaultDraft(c);
                                if (v) scheduleDefaultCurrencySave(v);
                              }}
                              getOptionLabel={(o) => formatCurrencyPickerLabel(o)}
                              disabled={!canManageCurrency || saving}
                              size="small"
                              sx={{ minWidth: 260, mt: 0.5 }}
                              renderInput={(params) => (
                                <TextField {...params} label="Organization default currency" placeholder="Search code or name" />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Reference vs EUR from platform
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {defaultCurrencyPlatformUpdatedAt
                              ? formatDateTime(defaultCurrencyPlatformUpdatedAt)
                              : '—'}
                          </TableCell>
                          <TableCell>Platform</TableCell>
                          <TableCell align="center">
                            <Tooltip title="The default currency leg always uses the platform EUR-based reference.">
                              <span>
                                <Switch checked disabled size="small" />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="History of default currency changes">
                              <IconButton
                                size="small"
                                aria-label="Default currency history"
                                onClick={() => openHistory('default', 'Organization default currency history')}
                                disabled={!defaultCurrency}
                              >
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>

                        {!defaultCurrency ? (
                          <TableRow>
                            <TableCell colSpan={6}>
                              <Typography variant="body2" color="text.secondary">
                                Choose an organization default currency first, then add other currencies.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          pairs.map((p) => (
                            <TableRow key={p.otherCurrency}>
                              <TableCell>{formatCurrencyPickerLabel(p.otherCurrency)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontFamily="monospace">
                                  {p.effectiveDefaultPer1Other ?? '—'}
                                </Typography>
                                {p.impliedDefaultPer1Other &&
                                  p.impliedDefaultPer1Other !== p.effectiveDefaultPer1Other && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Platform ref: {p.impliedDefaultPer1Other}
                                    </Typography>
                                  )}
                                {(p.missingPlatformReference || p.missingEffectiveLeg) && (
                                  <Typography variant="caption" color="warning.main" display="block">
                                    {p.missingPlatformReference && 'Missing platform data '}
                                    {p.missingEffectiveLeg && 'Incomplete rate '}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {p.rateUpdatedAt ? formatDateTime(p.rateUpdatedAt) : '—'}
                              </TableCell>
                              <TableCell>
                                {p.rateSource === 'override' ? 'Organization override' : 'Platform reference'}
                              </TableCell>
                              <TableCell align="center">
                                <Switch
                                  checked={p.followPlatformRate}
                                  onChange={(_, checked) => setFollowPlatform(p.otherCurrency, checked)}
                                  disabled={!canManageCurrency || saving}
                                  size="small"
                                  inputProps={{ 'aria-label': `Auto from server ${p.otherCurrency}` }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Pull from platform reference (clears org rate history for this currency)">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => pullFromPlatform(p.otherCurrency)}
                                      disabled={!canManageCurrency || saving}
                                      aria-label={`Pull ${p.otherCurrency} from server`}
                                    >
                                      <CloudDownloadIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Edit organization rate">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => openEditRate(p.otherCurrency)}
                                      disabled={!canManageCurrency || saving}
                                      aria-label={`Edit ${p.otherCurrency}`}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Rate history">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      openHistory(p.otherCurrency, `History — ${formatCurrencyPickerLabel(p.otherCurrency)}`)
                                    }
                                    aria-label={`History ${p.otherCurrency}`}
                                  >
                                    <HistoryIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove currency">
                                  <IconButton
                                    size="small"
                                    onClick={() => removeAdditional(p.otherCurrency)}
                                    disabled={!canManageCurrency || saving}
                                    aria-label={`Remove ${p.otherCurrency}`}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Disclaimers
                  </Typography>
                  {disclaimerKeys.map((k) => (
                    <Typography key={k} variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                      {DISCLAIMER_COPY[k] || k}
                    </Typography>
                  ))}
                </CardContent>
              </Card>

              <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add currency</DialogTitle>
                <DialogContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Pick a currency to pair with {defaultCurrency || 'your default'}.
                  </Typography>
                  <Autocomplete
                    options={additionalPickerOptions}
                    value={pendingAdditional}
                    onChange={(_, v) => setPendingAdditional(v)}
                    getOptionLabel={(o) => formatCurrencyPickerLabel(o)}
                    disabled={saving}
                    size="small"
                    fullWidth
                    sx={{ mt: 1 }}
                    renderInput={(params) => (
                      <TextField {...params} label="Currency" placeholder="Search code or name" autoFocus />
                    )}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                  <Button variant="contained" onClick={addAdditional} disabled={saving || !pendingAdditional}>
                    Add
                  </Button>
                </DialogActions>
              </Dialog>

              <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit rate — {overrideOther ? formatCurrencyPickerLabel(overrideOther) : ''}</DialogTitle>
                <DialogContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {defaultCurrency || 'Default'} per 1 unit of {overrideOther}. Saving turns off auto from server for this currency.
                  </Typography>
                  <TextField
                    label={`${defaultCurrency || 'Default'} per 1 other`}
                    value={overrideRate}
                    onChange={(e) => setOverrideRate(e.target.value)}
                    disabled={saving}
                    size="small"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Effective from"
                    type="datetime-local"
                    value={overrideFrom}
                    onChange={(e) => setOverrideFrom(e.target.value)}
                    disabled={saving}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText={dateTimeFieldHelperText}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Effective to (optional)"
                    type="datetime-local"
                    value={overrideTo}
                    onChange={(e) => setOverrideTo(e.target.value)}
                    disabled={saving}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText={dateTimeFieldHelperText}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                  <Button variant="contained" onClick={submitOverride} disabled={saving}>
                    Save
                  </Button>
                </DialogActions>
              </Dialog>

              <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{historyTitle}</DialogTitle>
                <DialogContent>
                  {historyRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No history entries yet.
                    </Typography>
                  ) : historyKind === 'default' ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>When</TableCell>
                          <TableCell>Change</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historyRows.map((row) => {
                          const det = row.details as { from?: string; to?: string } | null | undefined;
                          return (
                            <TableRow key={String(row.id)}>
                              <TableCell>{formatDateTime(String(row.createdAt))}</TableCell>
                              <TableCell>
                                {det?.from != null || det?.to != null ? (
                                  <Typography variant="body2">
                                    {String(det.from ?? '—')} → {String(det.to ?? '—')}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    {JSON.stringify(row.details)}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Default per 1 other</TableCell>
                          <TableCell>Effective from</TableCell>
                          <TableCell>Effective to</TableCell>
                          <TableCell>Recorded</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historyRows.map((row) => (
                          <TableRow key={String(row.id)}>
                            <TableCell>{String(row.displayDefaultPer1Other ?? '—')}</TableCell>
                            <TableCell>{formatDateTime(String(row.effectiveFrom))}</TableCell>
                            <TableCell>
                              {row.effectiveTo ? formatDateTime(String(row.effectiveTo)) : '—'}
                            </TableCell>
                            <TableCell>{formatDateTime(String(row.createdAt))}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setHistoryOpen(false)}>Close</Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Box>
      )}

      {tab === 2 && (
        <Box>
          {orgDateTimeExplainer}
          {!canManageCurrency && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Only organization owners and managers can change default rates.
            </Alert>
          )}
          {hourlyLoading ? (
            <LinearProgress />
          ) : (
            <>
              {renderHourlyTable(
                'EMPLOYEE_COST',
                'Employee cost (per hour)',
                'Internal cost per hour. Effective-dated rows; the latest effective from at a given time wins. Independent currency from billing.'
              )}
              {renderHourlyTable(
                'BILLING',
                'Billing rate (per hour)',
                'Client-facing rate per hour. Same rules as employee cost.'
              )}
              <Dialog open={hourlyDialogOpen} onClose={() => setHourlyDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{hourlyEditId ? 'Edit rate row' : 'Add rate row'}</DialogTitle>
                <DialogContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                    Kind: {hourlyDialogKind === 'EMPLOYEE_COST' ? 'Employee cost' : 'Billing rate'}
                  </Typography>
                  <TextField
                    label="Effective from"
                    type="datetime-local"
                    value={hourlyFormFrom}
                    onChange={(e) => setHourlyFormFrom(e.target.value)}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                    disabled={saving}
                    helperText={dateTimeFieldHelperText}
                  />
                  <TextField
                    label="Amount per hour"
                    value={hourlyFormAmount}
                    onChange={(e) => setHourlyFormAmount(e.target.value)}
                    fullWidth
                    margin="normal"
                    disabled={saving}
                    helperText="Decimal; 0 is allowed."
                  />
                  <Autocomplete
                    options={currencyCodes}
                    value={hourlyFormCurrency.length === 3 ? hourlyFormCurrency.toUpperCase() : null}
                    onChange={(_, v) => setHourlyFormCurrency(v ? v.toUpperCase() : '')}
                    getOptionLabel={(o) => formatCurrencyPickerLabel(o)}
                    disabled={saving}
                    sx={{ mt: 2 }}
                    renderInput={(params) => <TextField {...params} label="Currency" placeholder="Search code or name" />}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setHourlyDialogOpen(false)}>Cancel</Button>
                  <Button variant="contained" onClick={saveHourlyDialog} disabled={saving || !canManageCurrency}>
                    Save
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Box>
      )}

      {tab === 3 && organizationId && (
        <Box>
          {orgDateTimeExplainer}
          <WorkingCalendarsSettingsPanel organizationId={organizationId} canManage={canManageCurrency} />
        </Box>
      )}
    </Box>
  );
};

export default OrganizationSettings;
